import { 
  ProfileClassificationCode,
  SecurityTag,
} from "api/holdings/types";
import { EOrderClassType as SecurityClassType1 } from "components/TradingModals/useOrderTypeFields";
import { isProfessionalClassification } from "utils/userClassification";
import { 
  TradingType, 
  PortfolioTypeCode,
  AccessRights,
} from "./enums";

// Additional security tags not in API enum
const TRADEABLE = "Tradeable";
export const PROF_YES = "PROF-Yes";
export const SEC_TYPE_9 = "SecType:9";
export const SEC_TYPE_10 = "SecType:10";
export const SEC_TYPE_11 = "SecType:11";
export const SUBSCR_PORTAL_GROUP = "SubscrPortalGroup";

const REQUIRED_TRADEABLE_TAGS = [
  TRADEABLE,
];

const BLOCKED_TRADEABLE_TAGS = [
  SecurityTag.UNSA_YES,
  SecurityTag.NOOF_YES,
  SecurityTag.PRICE_EXPIRED,
  SecurityTag.PREMATURITY_EXPIRED,
  SecurityTag.TRADEABLE_BACK_ONLY,
];

export function determineTradingType(params: {
  isAnotherPrivateUser: boolean;
  isAnotherLegalUser: boolean;
  portfolioType?: string;
  accessRights: AccessRights;
  classification?: string;
  canTradeComplex: boolean;
}): TradingType {
  const { 
    isAnotherPrivateUser, 
    isAnotherLegalUser, 
    portfolioType, 
    accessRights,
    classification,
    canTradeComplex 
  } = params;

  // 1. No trading conditions
  if (
    isAnotherPrivateUser || 
    portfolioType === PortfolioTypeCode.CBL || 
    accessRights === AccessRights.VIEW
  ) {
    return TradingType.NO_TRADING;
  }

  const isRetail = !isProfessionalClassification(classification);

  // 2. Professional trading
  if (
    !isAnotherPrivateUser &&
    portfolioType === PortfolioTypeCode.BROKERAGE &&
    accessRights === AccessRights.FULL &&
    !isRetail
  ) {
    return TradingType.PROFESSIONAL_TRADING;
  }

  // 3. Retail non-complex trading (Legal entities without test)
  if (
    isAnotherLegalUser &&
    portfolioType === PortfolioTypeCode.BROKERAGE &&
    accessRights === AccessRights.FULL &&
    isRetail &&
    !canTradeComplex
  ) {
    return TradingType.RETAIL_NONCOMPLEX_TRADING;
  }

  // 4 & 5. Retail trading (Legal entities with test OR Private persons)
  if (
    portfolioType === PortfolioTypeCode.BROKERAGE &&
    accessRights === AccessRights.FULL &&
    isRetail
  ) {
    return TradingType.RETAIL_TRADING;
  }

  return TradingType.NO_TRADING;
}

export function isSecurityTradeable(security: { tagsAsList: string[] }): boolean {
  const tags = security.tagsAsList;
  
  // Check if has required tags
  const hasRequiredTags = REQUIRED_TRADEABLE_TAGS.every(tag => tags.includes(tag));
  
  // Check if has any blocked tags
  const hasBlockedTags = BLOCKED_TRADEABLE_TAGS.some(tag => tags.includes(tag));
  
  return hasRequiredTags && !hasBlockedTags;
}

export function hasSecurityTag(security: { tagsAsList: string[] }, tag: SecurityTag | string): boolean {
  return security.tagsAsList.includes(tag);
}

export function isSecurityComplex(security: { class1Name?: string }): boolean {
  return security.class1Name === SecurityClassType1.COMPLEX;
}

export function isSecurityNonComplex(security: { class1Name?: string }): boolean {
  return security.class1Name === SecurityClassType1.NON_COMPLEX;
}

export function hasEnoughCash(
  availableCash: number | undefined
): boolean {
  return typeof availableCash === 'number' && availableCash > 0;
}

export function isFutureOrTodayDate(dateString?: string | null): boolean {
  if (!dateString) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  
  return date >= today;
}

export function shouldShowCallToBrokerForSecType(security: { tagsAsList: string[] }): boolean {
  const tags = security.tagsAsList;
  const hasAnySecType = tags.some(tag => tag.startsWith("SecType:"));

  // Any security without SecType should show "Call to broker"
  if (!hasAnySecType) return true;

  return false;
}

export function isNonUCITSSecurity(
  classification?: string,
  security?: { tagsAsList: string[] }
): boolean {
  const retailClassifications = [
    ProfileClassificationCode.RET,
    ProfileClassificationCode.NPROF,
    ProfileClassificationCode.NA,
  ];
  
  return retailClassifications.includes(classification as ProfileClassificationCode) && 
         hasSecurityTag(security || { tagsAsList: [] }, SEC_TYPE_9);
}

export function isProfOnlySecurity(security: { tagsAsList: string[] }): boolean {
  return hasSecurityTag(security, PROF_YES);
}
